import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { cx } from '@/lib/clases'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { LineaEsqueleto } from '@/ui/Esqueleto'
import { useEstadoContabilidad } from '@/dominio/contabilidad/useContabilidad'
import { EncabezadoPantalla } from './-pantalla'
import { TableroContable } from './-contabilidad/Tablero'
import { AsientosContables } from './-contabilidad/Asientos'
import { CatalogoContable } from './-contabilidad/Catalogo'
import { ReglasContables } from './-contabilidad/Reglas'
import { PeriodosContables } from './-contabilidad/Periodos'
import { ReportesContables } from './-contabilidad/Reportes'
import { ConfiguracionContable } from './-contabilidad/Configuracion'

/**
 * La contabilidad (`docs/adr/0025-contabilidad.md`): el complemento que el
 * dueño enciende por contribuyente.
 *
 * Una pantalla con secciones, como la nómina: la sección y el asiento abierto
 * van en la URL para poder pasar el enlace a un asiento concreto.
 */

const SECCIONES = [
  { id: 'tablero', etiqueta: 'Tablero' },
  { id: 'asientos', etiqueta: 'Asientos' },
  { id: 'catalogo', etiqueta: 'Catálogo' },
  { id: 'reglas', etiqueta: 'Reglas' },
  { id: 'periodos', etiqueta: 'Períodos y cierres' },
  { id: 'reportes', etiqueta: 'Reportes' },
  { id: 'configuracion', etiqueta: 'Configuración' },
] as const

type IdSeccion = (typeof SECCIONES)[number]['id']

function esSeccion(valor: unknown): valor is IdSeccion {
  return SECCIONES.some((s) => s.id === valor)
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const Route = createFileRoute('/contabilidad')({
  validateSearch: (busqueda: Record<string, unknown>): { seccion?: IdSeccion; asiento?: string } => ({
    seccion: esSeccion(busqueda.seccion) && busqueda.seccion !== 'tablero' ? busqueda.seccion : undefined,
    asiento: typeof busqueda.asiento === 'string' && UUID.test(busqueda.asiento) ? busqueda.asiento : undefined,
  }),
  component: PantallaContabilidad,
})

function PantallaContabilidad() {
  const estado = useEstadoContabilidad()
  const navegar = useNavigate({ from: Route.fullPath })
  const busqueda = Route.useSearch()
  const seccion = busqueda.seccion ?? 'tablero'

  const irA = (s: IdSeccion) => void navegar({ search: { seccion: s === 'tablero' ? undefined : s }, replace: true })

  if (estado.isLoading) {
    return <LineaEsqueleto ancho="w-1/2" />
  }

  if (!estado.data?.contratada) {
    return (
      <div className="flex max-w-[900px] flex-col gap-4">
        <EncabezadoPantalla titulo="Contabilidad" />
        <EstadoVacio
          variante="primera-vez"
          mensaje="La contabilidad es un complemento: lleva el diario y el mayor solos a partir de lo que ya factura, recibe, paga en nómina y vende en caja, y saca los estados financieros. Para encenderla, hable con Agentical Group."
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Contabilidad"
        descripcion="Los asientos salen solos de los comprobantes, la nómina y la caja; usted revisa, ajusta y cierra."
      />

      <nav aria-label="Secciones de la contabilidad" className="overflow-x-auto border-b border-gris-300">
        <ul className="flex gap-1">
          {SECCIONES.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                aria-current={seccion === s.id ? 'page' : undefined}
                onClick={() => irA(s.id)}
                className={cx(
                  'inline-flex h-[var(--alto-control)] items-center whitespace-nowrap border-b-2 px-3 text-cuerpo',
                  seccion === s.id ? 'border-marca font-medium text-marca' : 'border-transparent text-gris-600 hover:text-gris-900',
                )}
              >
                {s.etiqueta}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {seccion === 'tablero' ? <TableroContable irA={irA} /> : null}
      {seccion === 'asientos' ? (
        <AsientosContables
          asientoAbierto={busqueda.asiento ?? null}
          abrirAsiento={(id) => void navegar({ search: { seccion: 'asientos', asiento: id ?? undefined }, replace: true })}
        />
      ) : null}
      {seccion === 'catalogo' ? <CatalogoContable /> : null}
      {seccion === 'reglas' ? <ReglasContables /> : null}
      {seccion === 'periodos' ? <PeriodosContables /> : null}
      {seccion === 'reportes' ? <ReportesContables /> : null}
      {seccion === 'configuracion' ? <ConfiguracionContable /> : null}
    </div>
  )
}
